/**
 * CRUD helpers for Group Template HealthcareServices.
 */
import type { MedplumClient } from '@medplum/core';
import type { HealthcareService } from '@medplum/fhirtypes';

import {
  GROUP_TEMPLATE_CATEGORY_CODE,
  GROUP_TEMPLATE_CATEGORY_SYSTEM,
} from '@unify/fhir-model/constants';
import type { GroupEnrollmentMode, GroupSessionModality } from '@unify/fhir-model/resources/group';
import {
  getGroupTemplateCapacity,
  getGroupTemplateDayOfWeek,
  getGroupTemplateDescription,
  getGroupTemplateEnrollmentMode,
  getGroupTemplateModality,
  getGroupTemplatePhotoUrl,
  getGroupTemplateSessionCount,
  getGroupTemplateSessionHealthcareService,
  getGroupTemplateTimezone,
  healthcareServiceRef,
  isGroupTemplateHealthcareService,
  setGroupTemplateCapacity,
  setGroupTemplateDayOfWeek,
  setGroupTemplateDescription,
  setGroupTemplateEnrollmentMode,
  setGroupTemplateModality,
  setGroupTemplatePhoto,
  setGroupTemplateSessionCount,
  setGroupTemplateSessionHealthcareService,
  setGroupTemplateTimezone,
  weekdayNamesFromDayOfWeekCodes,
  withGroupTemplateCategory,
} from '@unify/fhir-model/resources/group-template';
import type { DayOfWeekCode } from '@unify/fhir-model/resources/schedule';

import { uploadGroupImage } from './groupImageService';
import { resolveGroupTherapyHealthcareServices } from './resolveGroupHealthcareServices';
import type { GroupEnrollmentMode as UiEnrollmentMode, GroupSetupFormValues } from './types';

export type GroupTemplateFormValues = {
  id?: string;
  active: boolean;
  name: string;
  comment?: string;
  imageUrl?: string;
  organizationId?: string;
  organizationName?: string;
  /** Auto-stamped Group Therapy catalog HCS; not shown in Templates UI. */
  sessionHealthcareServiceReference?: string;
  sessionHealthcareServiceDisplay?: string;
  enrollmentMode: UiEnrollmentMode;
  minEnrollment: number;
  maxEnrollment: number;
  modality?: GroupSessionModality;
  timezone?: string;
  /** Default "Appointments in series" for new cohorts. */
  sessionCount?: number;
  /** Default meeting day as a FHIR DaysOfWeek code (`mon` … `sun`). */
  dayOfWeek?: DayOfWeekCode;
};

export type GroupTemplateListItem = GroupTemplateFormValues & { reference: string };

function bareOrgId(ref: string | undefined): string | undefined {
  if (!ref) return undefined;
  return ref.startsWith('Organization/') ? ref.slice('Organization/'.length) : ref;
}

function hsRef(ref: string | undefined, display?: string) {
  const trimmed = ref?.trim();
  if (!trimmed) return undefined;
  const id = trimmed.startsWith('HealthcareService/')
    ? trimmed.slice('HealthcareService/'.length)
    : trimmed;
  return healthcareServiceRef(id, display);
}

async function withUploadedImage(
  medplum: MedplumClient,
  values: GroupTemplateFormValues,
  imageFile?: File | null,
): Promise<GroupTemplateFormValues> {
  if (!imageFile) return values;
  if (!values.organizationId) throw new Error('Practice is required before uploading a group image.');
  return {
    ...values,
    imageUrl: await uploadGroupImage(medplum, imageFile, values.organizationId),
  };
}

/**
 * Ensure session HCS is the platform Group Therapy catalog row.
 * Screening HCS is never linked on templates.
 */
async function withCatalogSessionHealthcareService(
  medplum: MedplumClient,
  values: GroupTemplateFormValues,
): Promise<GroupTemplateFormValues> {
  if (values.sessionHealthcareServiceReference) return values;
  const refs = await resolveGroupTherapyHealthcareServices(medplum);
  if (!refs.session) {
    throw new Error(
      'Group Therapy HealthcareService is missing from the platform catalog. Run seed:service-catalog.',
    );
  }
  return {
    ...values,
    sessionHealthcareServiceReference: refs.session.reference,
    sessionHealthcareServiceDisplay: refs.session.display,
  };
}

export function templateResourceToFormValues(svc: HealthcareService): GroupTemplateFormValues {
  const capacity = getGroupTemplateCapacity(svc);
  const session = getGroupTemplateSessionHealthcareService(svc);
  return {
    id: svc.id,
    active: svc.active !== false,
    name: svc.name ?? '',
    comment: getGroupTemplateDescription(svc),
    imageUrl: getGroupTemplatePhotoUrl(svc),
    organizationId: bareOrgId(svc.providedBy?.reference),
    organizationName: svc.providedBy?.display,
    sessionHealthcareServiceReference: session?.reference,
    sessionHealthcareServiceDisplay: session?.display,
    enrollmentMode: (getGroupTemplateEnrollmentMode(svc) as UiEnrollmentMode | undefined) ?? 'closed',
    minEnrollment: capacity.minEnrollment ?? 4,
    maxEnrollment: capacity.maxEnrollment ?? 10,
    modality: getGroupTemplateModality(svc),
    timezone: getGroupTemplateTimezone(svc) ?? 'America/Los_Angeles',
    sessionCount: getGroupTemplateSessionCount(svc),
    dayOfWeek: getGroupTemplateDayOfWeek(svc),
  };
}

export function formValuesToTemplateResource(
  values: GroupTemplateFormValues,
  existing?: HealthcareService,
): HealthcareService {
  const imageUrl = values.imageUrl?.trim();
  let svc = withGroupTemplateCategory({
    ...(existing ?? { resourceType: 'HealthcareService' }),
    resourceType: 'HealthcareService',
    id: values.id ?? existing?.id,
    active: values.active,
    name: values.name.trim(),
    providedBy: values.organizationId
      ? {
          reference: `Organization/${bareOrgId(values.organizationId)}`,
          display: values.organizationName,
        }
      : existing?.providedBy,
  });
  svc = setGroupTemplateDescription(svc, values.comment);
  svc = setGroupTemplatePhoto(
    svc,
    imageUrl
      ? { url: imageUrl, ...(imageUrl.startsWith('Binary/') ? { contentType: 'image/jpeg' } : {}) }
      : undefined,
  );
  svc = setGroupTemplateSessionHealthcareService(
    svc,
    hsRef(values.sessionHealthcareServiceReference, values.sessionHealthcareServiceDisplay),
  );
  svc = setGroupTemplateEnrollmentMode(svc, values.enrollmentMode as GroupEnrollmentMode);
  svc = setGroupTemplateCapacity(svc, {
    minEnrollment: values.minEnrollment,
    maxEnrollment: values.maxEnrollment,
  });
  svc = setGroupTemplateModality(svc, values.modality);
  svc = setGroupTemplateTimezone(svc, values.timezone);
  svc = setGroupTemplateSessionCount(svc, values.sessionCount);
  svc = setGroupTemplateDayOfWeek(svc, values.dayOfWeek);
  return svc;
}

export async function searchGroupTemplates(
  medplum: MedplumClient,
  organizationId?: string,
): Promise<GroupTemplateListItem[]> {
  medplum.invalidateSearches('HealthcareService');
  const org = bareOrgId(organizationId);
  const orgParam = org ? { organization: `Organization/${org}` } : {};
  let resources = await medplum.searchResources('HealthcareService', {
    _count: '200',
    'service-category': `${GROUP_TEMPLATE_CATEGORY_SYSTEM}|${GROUP_TEMPLATE_CATEGORY_CODE}`,
    ...orgParam,
  });
  if (!resources.length) {
    resources = await medplum.searchResources('HealthcareService', { _count: '200', ...orgParam });
  }
  return resources
    .filter(isGroupTemplateHealthcareService)
    .filter((r) => r.id)
    .map((r) => ({ ...templateResourceToFormValues(r), reference: `HealthcareService/${r.id}` }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function createGroupTemplate(
  medplum: MedplumClient,
  values: GroupTemplateFormValues,
  imageFile?: File | null,
): Promise<HealthcareService> {
  const withImage = await withUploadedImage(medplum, values, imageFile);
  const next = await withCatalogSessionHealthcareService(medplum, withImage);
  return medplum.createResource(formValuesToTemplateResource(next));
}

export async function updateGroupTemplate(
  medplum: MedplumClient,
  values: GroupTemplateFormValues,
  imageFile?: File | null,
): Promise<HealthcareService> {
  if (!values.id) throw new Error('Template id is required to update.');
  const existing = await medplum.readResource('HealthcareService', values.id);
  const withImage = await withUploadedImage(medplum, values, imageFile);
  const next = await withCatalogSessionHealthcareService(medplum, withImage);
  return medplum.updateResource(formValuesToTemplateResource(next, existing));
}

/** Seed cohort form fields from a template. UI defaults start/end to 6–7 PM. */
export function seedGroupFormFromTemplate(
  template: GroupTemplateFormValues,
): Partial<GroupSetupFormValues> {
  if (!template.id) return {};
  return {
    name: template.name,
    imageUrl: template.imageUrl,
    description: template.comment,
    organizationId: template.organizationId,
    organizationName: template.organizationName,
    templateReference: `HealthcareService/${template.id}`,
    templateDisplay: template.name,
    programReference: template.sessionHealthcareServiceReference,
    programDisplay: template.sessionHealthcareServiceDisplay,
    enrollmentMode: template.enrollmentMode,
    minEnrollment: template.minEnrollment,
    maxEnrollment: template.maxEnrollment,
    modality: template.modality,
    timezone: template.timezone,
    sessionCount: template.sessionCount ?? 8,
    // Cohort schedules store English weekday names; templates store FHIR codes.
    daysOfWeek: template.dayOfWeek ? weekdayNamesFromDayOfWeekCodes([template.dayOfWeek]) : [],
    startTime: '18:00',
    endTime: '19:00',
    recurrence: 'weekly',
  };
}

/** Join template display fields onto a cohort list/form item. */
export function joinGroupWithTemplate<T extends Partial<GroupSetupFormValues>>(
  group: T,
  template: GroupTemplateFormValues | undefined,
): T & Partial<GroupSetupFormValues> {
  if (!template) return group;
  return {
    ...group,
    imageUrl: group.imageUrl || template.imageUrl,
    description: group.description || template.comment,
    // Cohort override when stored; otherwise template default.
    enrollmentMode: group.enrollmentModeStored
      ? (group.enrollmentMode ?? template.enrollmentMode)
      : (template.enrollmentMode ?? group.enrollmentMode),
    templateDisplay: group.templateDisplay || template.name,
    programReference: template.sessionHealthcareServiceReference,
    programDisplay: template.sessionHealthcareServiceDisplay,
  };
}
